import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from "react";
import _ from "lodash";
import classnames from "classnames/bind";
import analyseData from "../../../../utils/analyseSearchData";
import Filter from "../../../../utils/filter";
import {
  ISearchVehicle as IVehicle,
  IVehicleType,
  Replacement,
} from "../../../../interface";
import QuickSearchInput from "../QuickSearchInput";
import ToggleFilter from "../../../ToggleFilter";
import FilterDropdown from "../../FilterDropdown";
import FilterResult from "../../FilterResult";
import style from "./index.module.scss";
import instance from "../../../../services/httpRequest";
import Loading from "../../../Loading";
import localesReplacement from "../../../../utils/localesReplacement";
import { isQuickSearchDisabled } from "../../../../utils/isQuickSearchDisabled";
import { useDebounce } from "use-debounce";
import {
  APPNAME,
  FASTSCAN,
  INIT_FILTER_DROPDOWN,
  ANALYTICS_EVENT,
  ANALYTICS_PAGE_TYPE,
  ANALYTICS_SEARCH_TYPE,
  ANALYTICS_USER_TYPE,
} from "../../../../constants";

import ClickOut from "react-click-out";
import { Dispatch } from "redux";
import actions from "../../../../redux/actions";
import { connect } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useSearchParams } from "react-router-dom";
import { useAppName } from "../../../../hooks/use-app-names";
import { useAnalytics } from "../../../../hooks/use-analytics";

interface IProps {
  country?: string;
  language: string | undefined;
  replacements: Replacement;
  onVehicleSelect?: (
    value: string | number,
    model: string,
    vehicleType?: string
  ) => void;
  switchVehicleSearch: () => void;
  licence: boolean;
  licensePlateRegexes: string;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
  userType?: boolean;
  captcha: boolean;
  isExtendedRestApi: boolean;
  setFilterResults: (v: IVehicle[] | null) => void;
  setLoading: (v: boolean) => void;
  setLicenseKeys: (v: string) => void;
  searchHandler: () => void;
}

const cx = classnames.bind(style);
const classDropdownItem = "dropdown-item";
interface IFilterCategories {
  categoriesTypes: string[];
  makeTypes: string[];
  modelTypes: string[];
  yearTypes: string[];
}

function quickSearch(
  value: string,
  isPersonalizedLicensePlate: boolean,
  userType = false,
  country?: string,
  signal?: AbortSignal,
  category?: string,
  make?: string,
  model?: string,
  year?: string,
  sendAnalytics?: any
) {
  return instance
    .get(
      `/quickSearch?text=${encodeURIComponent(value)}${isPersonalizedLicensePlate ? "&isPersonalizedLicensePlate=true" : ""
      }${category && "&categories=" + category}${make && "&makes=" + make}${model && "&models=" + model
      }${year && "&years=" + year}`,
      {
        signal: signal,
      }
    )
    .then((d: any) => {
      const sortedData = _.orderBy(
        d.data,
        ["yearTo", "yearFrom"],
        ["desc", "desc"]
      );
      sendAnalytics({
        event: d.data?.length
          ? ANALYTICS_EVENT.QUICK_SEARCH
          : ANALYTICS_EVENT.QUICK_SEARCH_UNAVAILABLE,
        search: ANALYTICS_SEARCH_TYPE.QUICK_SEARCH,
        page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
        query: value,
        user: userType
          ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER
          : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
        country: country,
      });
      return { ...d, data: sortedData };
    });
}

export interface QuickSearchRef {
  clearValue: () => void;
  value: string;
  set: any;
}

const QuickSearch = forwardRef(function QuickSearch(
  props: IProps,
  ref: Ref<QuickSearchRef>
) {
  const {
    onVehicleSelect,
    replacements,
    country,
    switchVehicleSearch,
    licence,
    licensePlateRegexes,
    selectedWorkshop,
    restore,
    preserved,
    userType,
    captcha,
    language,
    isExtendedRestApi,
    setFilterResults,
    setLoading,
    setLicenseKeys,
    searchHandler,
  } = props;
  const sendAnalytics = useAnalytics();
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1024 });
  const isFastScan = useAppName(FASTSCAN);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [vehicleTypes, setVehicleTypes] = useState<IVehicleType[]>([]);
  const [vehicleType, setVehicleType] = useState<string>("");
  const [filterTypeValues, setFilterTypeValues] =
    useState<IFilterCategories>(INIT_FILTER_DROPDOWN);
  const [make, setMake] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [searchKey, setSearchKey] = useState<string>("");
  const [results, setResults] = useState<IVehicle[]>([]);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const [isPersonalizedLicensePlate, setIsPersonalizedLicensePlate] =
    useState(false);
  const controller = new AbortController();
  const regExp = new RegExp(licensePlateRegexes).test(searchKey);
  const [debounceKey] = useDebounce(searchKey, 1000);
  //Get licence search params from API
  useEffect(() => {
    return () => {
      _.unset(instance.defaults.headers, "Authorization");
      setResults([]);
      setFilterResults(null);
    };
  }, []);
  useEffect(() => {
    memoAutocompleted(debounceKey);
  }, [debounceKey, country]);

  /* Quick search input change & clear function */
  const changeHandler = (value: string, token?: string) => {
    if (token) {
      _.assign(instance.defaults.headers, {
        Authorization: `Captcha ${token}`,
      });
    }
    if (_.trim(value).length >= 2) {
      setIsMount(true);
      setLoading(true);
      setVisible(true);
      searchHandler();
      if (isDesktopOrLaptop) {
        searchParams.delete("id");
        searchParams.set("page", "landing");
        isFastScan && searchParams.set(APPNAME, FASTSCAN);
        setSearchParams(searchParams);
      }
    }
    setSearchKey(value);
    restore && sessionStorage.setItem("searchKey", value);
    setIsPersonalizedLicensePlate(false);
  };

  const dealWithResults = (results: IVehicle[], value: string) => {
    if (_.isEmpty(results)) {
      setFilterResults(null);
      setLicenseKeys(`${localesReplacement(
        replacements,
        "No results found for"
      )}
        <strong>${_.trim(value)}</strong>`);
    } else {
      setFilterResults(results);
    }
  };

  const getQuickSearchData = (
    value: string,
    isPersonalizedLicensePlate: boolean,
    vehicleType: string,
    make: string,
    model: string,
    year: string
  ) => {
    quickSearch(
      value,
      isPersonalizedLicensePlate,
      userType,
      country,
      controller.signal,
      vehicleType,
      make,
      model,
      year,
      sendAnalytics
    ).then((d) => {
      if (_.inRange(d.status, 200, 300)) {
        if (isExtendedRestApi) {
          setVehicleTypes(analyseData.vehicleTypeFormat(d.data?.results));
          setResults(d.data?.results);
          setFilterTypeValues((prev) => {
            return {
              ...prev,
              makeTypes: analyseData.getValues(d.data?.makes, true) ?? [],
              modelTypes: analyseData.getValues(d.data?.models, true) ?? [],
              categoriesTypes:
                analyseData.getValues(d.data?.categories, true) ?? [],
              yearTypes: analyseData.getValues(d.data?.years, true) ?? [],
            };
          });
          dealWithResults(d.data?.results, value);
        } else {
          setVehicleTypes(analyseData.vehicleTypeFormat(d.data));
          setResults(d.data);
          dealWithResults(d.data, value);
        }
      }
      setIsMount(false);
      setLoading(false);
      setFinish(true);
    });
  };

  const memoAutocompleted = useCallback(
    (
      value: string,
      isPersonalizedLicensePlate = false,
      vehicleType = "",
      make = "",
      model = "",
      year = ""
    ) => {
      if (_.trim(value).length >= 2) {
        setFinish(false);
        setIsMount(true);
        setLoading(true);
        setFilterResults([]);
        selectedWorkshop
          ? _.assign(instance.defaults.headers, {
            isWorkshopOwner: selectedWorkshop,
          })
          : _.unset(instance.defaults.headers, "isWorkshopOwner");
        getQuickSearchData(
          value,
          isPersonalizedLicensePlate,
          vehicleType,
          make,
          model,
          year
        );
      } else {
        setIsMount(false);
        setLoading(false);
        setFinish(false);
        setFilterVisible(false);
        setResults([]);
        setFilterResults(null);
        setFilterTypeValues(INIT_FILTER_DROPDOWN);
      }
      setVehicleType("");
      setMake("");
      setModel("");
      setYear("");
      setLicenseKeys("");
      setFilterTypeValues(INIT_FILTER_DROPDOWN);
    },
    [selectedWorkshop, isExtendedRestApi]
  );
  const mouseDownHandler = (value: string) => {
    if (_.trim(value).length >= 3) {
      setVisible(true);
    }
  };

  const clearQuickSearch = () => {
    setSearchKey("");
    memoAutocompleted("");
  };
  /* Vehicle Type dropdown change */
  const changeVehicleType = (vehicleType: string) => {
    setVehicleType(vehicleType);
    setMake("");
    setModel("");
    setYear("");
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year
      );
    restore && sessionStorage.setItem("searchQType", vehicleType);
  };
  /* Manufacturer dropdown change */
  const changeMake = (make: string) => {
    setMake(make);
    setModel("");
    setYear("");
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year
      );
    restore && sessionStorage.setItem("searchQType", vehicleType);
  };
  /* Model dropdown change */
  const changeModel = (model: string) => {
    setModel(model);
    setYear("");
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year
      );
    restore && sessionStorage.setItem("searchQType", vehicleType);
  };
  /* Year dropdown change */
  const changeYear = (year: string) => {
    setYear(year);
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year
      );
    restore && sessionStorage.setItem("searchQYear", year);
  };
  const searchByPlate = () => {
    memoAutocompleted(searchKey, true);
    setIsPersonalizedLicensePlate(true);
  };

  useEffect(() => {
    memoAutocompleted(searchKey);
    setResults([]);
    setFilterResults(null);
    setVehicleType("");
    setMake("");
    setModel("");
    setYear("");
  }, [selectedWorkshop]);

  useEffect(() => {
    if (preserved) {
      setSearchKey(sessionStorage.getItem("searchKey") ?? "");
    }
  }, [preserved]);
  useEffect(() => {
    if (preserved && results) {
      setVehicleType(sessionStorage.getItem("searchQType") ?? "");
      setMake(sessionStorage.getItem("searchQMake") ?? "");
      setModel(sessionStorage.getItem("searchQmodel") ?? "");
      setYear(sessionStorage.getItem("searchQYear") ?? "");
    }
  }, [preserved, results]);

  const SEARCHFORNUMBERPLATE = (
    <div className="guide">
      <div className="infor">
        <p>
          {localesReplacement(replacements, "No results found for")}
          <strong>{_.trim(searchKey)}</strong>
        </p>
      </div>
      <div className="guide-block">
        <p>
          {localesReplacement(replacements, "If")}
          <strong>{_.trim(searchKey)}</strong>
          {localesReplacement(
            replacements,
            "is a personalised license plate, try clicking Search by Number plate below."
          )}
        </p>
        <div className="buttons">
          <button className="primary" onClick={searchByPlate}>
            {localesReplacement(replacements, "Search by the Number plate")}
          </button>
          <button onClick={clearQuickSearch}>
            {localesReplacement(replacements, "Start New Search")}
          </button>
        </div>
      </div>
    </div>
  );

  const EMPTYFORNUMBERPLATE = (
    <div className="guide guide-empty">
      <div className="infor">
        <p>
          {localesReplacement(replacements, "No results found for")}
          <strong>{_.trim(searchKey)}</strong>
          {localesReplacement(
            replacements,
            ". Try searching using the Vehicle Search"
          )}
        </p>
      </div>
      <div className="guide-block">
        <div className="buttons">
          <button className="primary" onClick={switchVehicleSearch}>
            {localesReplacement(replacements, "Vehicle Search")}
          </button>
          <button onClick={clearQuickSearch}>
            {localesReplacement(replacements, "Start New Search")}
          </button>
        </div>
      </div>
    </div>
  );

  const renderNoMatch = () =>
    finish &&
    _.trim(searchKey).length >= 3 &&
    (!_.isEqual(country, "USA") && licence ? (
      <div className="no-match">
        {isPersonalizedLicensePlate || regExp
          ? EMPTYFORNUMBERPLATE
          : SEARCHFORNUMBERPLATE}
      </div>
    ) : (
      <div className="no-match">{EMPTYFORNUMBERPLATE}</div>
    ));

  useEffect(() => {
    if (isExtendedRestApi && !!filterTypeValues) {
      if (filterTypeValues.categoriesTypes.length === 1) {
        setVehicleType(filterTypeValues.categoriesTypes[0]);
      } else if (filterTypeValues.makeTypes.length === 1) {
        setMake(filterTypeValues.makeTypes[0]);
      } else if (filterTypeValues.modelTypes.length === 1) {
        setModel(filterTypeValues.modelTypes[0]);
      } else if (filterTypeValues.yearTypes.length === 1) {
        setYear(filterTypeValues.yearTypes[0]);
      }
    }
  }, [filterTypeValues, isExtendedRestApi]);

  const vehicleTypeDataList = isExtendedRestApi
    ? filterTypeValues?.categoriesTypes ?? []
    : analyseData.vehicleType(vehicleTypes);

  const makeTypeDataList = isExtendedRestApi
    ? filterTypeValues?.makeTypes ?? []
    : vehicleType
      ? analyseData.make(vehicleTypes, vehicleType)
      : analyseData.getMake(vehicleTypes);

  const modeTypeDataList = isExtendedRestApi
    ? filterTypeValues?.modelTypes ?? []
    : analyseData.model(vehicleTypes, vehicleType, make);

  const yearDataList = isExtendedRestApi
    ? filterTypeValues?.yearTypes ?? []
    : analyseData.getYear(
      new Filter(results)
        .vehicleType(vehicleType)
        .manufacturer(make)
        .model(model)
        .value()
    );

  const renderResults = () => {
    return !_.isEmpty(results) ? (
      <>
        <div hidden>
          <ToggleFilter
            toggleHandler={setFilterVisible}
            panelVisible={filterVisible}
          />
        </div>
        <div className={cx("dropdown-list", `${licence ? "licence" : ""}`)}>
          {results.length > 1 && (
            <>
              {!!vehicleTypes && (
                <>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={vehicleTypeDataList}
                      filter={
                        filterTypeValues.categoriesTypes.length === 1
                          ? filterTypeValues.categoriesTypes[0]
                          : vehicleType
                      }
                      changeHandler={changeVehicleType}
                      type={localesReplacement(replacements, "Vehicle Type")}
                      replacements={replacements}
                    />
                  </div>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={makeTypeDataList}
                      filter={
                        filterTypeValues.makeTypes.length === 1
                          ? filterTypeValues.makeTypes[0]
                          : make
                      }
                      changeHandler={changeMake}
                      type={localesReplacement(replacements, "Make")}
                      replacements={replacements}
                    />
                  </div>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={modeTypeDataList}
                      filter={
                        filterTypeValues.modelTypes.length === 1
                          ? filterTypeValues.modelTypes[0]
                          : model
                      }
                      changeHandler={changeModel}
                      type={localesReplacement(replacements, "Model")}
                      replacements={replacements}
                    />
                  </div>
                </>
              )}
              {!!results && (
                <>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={yearDataList}
                      filter={
                        filterTypeValues.yearTypes.length === 1
                          ? filterTypeValues.yearTypes[0]
                          : year
                      }
                      changeHandler={changeYear}
                      type={localesReplacement(replacements, "Year")}
                      replacements={replacements}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <FilterResult
          key={JSON.stringify(results)}
          dataList={results}
          vehicleType={vehicleType}
          make={make}
          model={model}
          year={year}
          sort={true}
          value={searchKey}
          onSelect={(va, me, ve) => {
            onVehicleSelect && onVehicleSelect(va, me, ve);
            setVisible(false);
          }}
          replacements={replacements}
        />
      </>
    ) : (
      renderNoMatch()
    );
  };

  const [visible, setVisible] = useState(false);

  useImperativeHandle(
    ref,
    () => {
      return {
        set: setSearchKey,
        clearValue() {
          if (searchKey) {
            clearQuickSearch();
            if (isDesktopOrLaptop) {
              searchParams.delete("id");
              searchParams.set("page", "landing");
              isFastScan && searchParams.set(APPNAME, FASTSCAN);
              setSearchParams(searchParams);
            }
          }
        },
        value: searchKey,
      };
    },
    [searchKey]
  );

  return (
    <div className={cx("wrapper")}>
      <ClickOut className="click-out" onClickOut={() => setVisible(false)}>
        {country && !isQuickSearchDisabled(country) && (
          <>
            <label>
              {localesReplacement(
                replacements,
                "Type to search or select from the dropdown below"
              )}
            </label>
            <QuickSearchInput
              value={searchKey}
              onChangeHandler={changeHandler}
              onClearHandler={clearQuickSearch}
              onMouseDownHandler={mouseDownHandler}
              licence={licence}
              captcha={captcha}
              replacements={replacements}
              country={country}
              language={language}
            />
            {visible && (
              <div className={`flyout ${cx("flyout")}`}>
                {!isMount ? renderResults() : <Loading />}
              </div>
            )}
          </>
        )}
      </ClickOut>
    </div>
  );
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setFilterResults: (vehicle: IVehicle[] | null) =>
    dispatch(actions.setResults(vehicle)),
  setLoading: (v: boolean) => dispatch(actions.setLoading(v)),
  setLicenseKeys: (v: string) => dispatch(actions.setLicenseKeys(v)),
});

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(
  QuickSearch
);
